import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Btn } from "@/components/erp/ui";
import { FinEffect } from "@/components/erp/finishing/FinEffect";
import { BACKEND } from "@/components/erp/prod/ProdInputs";
import { FinMy, procLabel } from "@/components/erp/finishing/FinMy";
import { FinWoCard } from "@/components/erp/finishing/FinWoCard";
import { FinAddWo, FinSettings, BonusRecap, WeeklyRecap } from "@/components/erp/finishing/FinAdmin";
import { TeamPrPanel } from "@/components/erp/purchasing/TeamPrPanel";
import { HelpPanel } from "@/components/erp/DesignExtras";

const ST = { WORKING: ["bg-emerald-100 text-emerald-800", "sedang kerja"], IDLE: ["bg-amber-100 text-amber-800", "jeda / istirahat"], NOT_STARTED: ["bg-slate-100 text-slate-500", "belum mulai"] };

const Members = ({ d }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4" data-testid="fin-members">
    <div className="font-semibold mb-2">Tim finishing</div>
    {!d.members.length && <p className="text-sm text-slate-400">Belum ada akun dengan role finishing.</p>}
    <div className="flex flex-wrap gap-2">
      {d.members.map((m) => (
        <div key={m.email} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid={`fin-member-${m.email}`}>
          <b>{m.name}</b>{m.head && <span className="ml-1 text-[10px] px-1.5 rounded bg-slate-900 text-white">KEPALA</span>}
          <span className={`ml-2 text-[11px] px-2 py-0.5 rounded-full ${ST[m.status][0]}`}>{ST[m.status][1]}</span>
          {m.active && <div className="text-[11px] text-emerald-700">{procLabel(d.processes, m.active.process)} · {m.active.wo_code}</div>}
        </div>
      ))}
    </div>
  </div>
);

const Team = ({ d }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4" data-testid="fin-team">
    <div className="font-semibold mb-2">Tim hari ini</div>
    {!d.team.length && <p className="text-sm text-slate-400">Belum ada yang mulai kerja hari ini.</p>}
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {d.team.map((t) => (
        <div key={t.name} className={`rounded-xl p-3 border ${t.break_warn ? "border-rose-300 bg-rose-50" : "border-slate-200"}`} data-testid={`fin-team-${t.name}`}>
          <div className="font-semibold">{t.name}</div>
          <div className="text-xs">{t.active ? <span className="text-emerald-700">● {procLabel(d.processes, t.active.process)} · {t.active.wo_code}</span> : <span className="text-slate-400">{t.not_started ? "belum mulai hari ini" : "tidak sedang mengerjakan"}</span>}</div>
          <div className="text-xs text-slate-600 mt-1">Kerja {Math.floor(t.minutes / 60)}j {t.minutes % 60}m · istirahat/jeda {Math.floor(t.break_min / 60)}j {t.break_min % 60}m · efektif {t.eff_pct}%</div>
          {t.by_kind && <div className="text-[11px] text-slate-500" data-testid={`fin-team-kinds-${t.name}`}>{Object.entries(t.by_kind).map(([k, m]) => `${d.breaks.find((b) => b.key === k)?.label || k} ${m}m`).join(" · ")}{t.on_break ? " · sedang istirahat" : ""}</div>}
          {t.break_excess && !t.break_warn && <div className="text-[11px] text-amber-700 font-semibold">Istirahat lewat {d.settings.break_free_min} mnt — kelebihan dipotong dari lembur</div>}
          {t.break_warn && <div className="text-[11px] text-rose-700 font-semibold">Istirahat lebih dari {d.settings.break_max_min} mnt — lembur ditolak</div>}
        </div>
      ))}
    </div>
  </div>
);

export default function Finishing() {
  const [d, setD] = useState(null); const [wo, setWo] = useState(null); const [sp] = useSearchParams();
  const load = useCallback(() => api.get("/finishing/board").then(({ data }) => setD(data)), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => { clearInterval(t); }; }, [load]);
  useEffect(() => { const id = sp.get("wo"); if (d && id && !wo) setWo(d.wos.find((x) => x.wo_id === id) || null); }, [d, sp]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="p-6 text-sm text-slate-400">Memuat…</div>;
  const cur = wo && d.wos.find((x) => x.wo_id === wo.wo_id);
  const qr = () => window.open(`${BACKEND}/api/finishing/qr.pdf?base=${encodeURIComponent(window.location.origin)}&ids=${d.wos.map((w) => w.wo_id).join(",")}`, "_blank");
  return (
    <div data-testid="finishing-page">
      <PageHeader title="Dashboard Finishing" subtitle={(d.processes || []).map((p) => p.label).join(" · ")}
        action={d.is_head && d.wos.length > 0 ? <Btn variant="ghost" data-testid="fin-print-qr" onClick={qr}>Cetak QR WO</Btn> : null} />
      <TeamPrPanel />
      <div className="mb-5"><HelpPanel /></div>
      <FinMy d={d} wo={cur || wo} setWo={setWo} load={load} />
      <div className="mt-6"><Members d={d} />{d.is_head && <Team d={d} />}</div>
      {d.is_head && <FinEffect />}
      <FinAddWo d={d} load={load} />
      <div className="space-y-3" data-testid="fin-cards">{(cur ? [cur] : d.wos).map((w) => <FinWoCard key={w.wo_id} w={w} d={d} load={load} />)}</div>
      <p className="text-[11px] text-slate-400 mt-4" data-testid="fin-rules">Skor = rata-rata skor biaya & waktu (50 = pas target, naik bila lebih hemat). Bonus {d.settings.bonus_pct}% dari anggaran finishing dibagi rata ke yang mengerjakan bila skor ≥ {d.settings.min_score}%, tidak lewat target jam & tidak melebihi anggaran. Lembur karena kesalahan sendiri tidak dibayar & tanpa uang makan. Lembur finishing otomatis diajukan untuk disetujui HR lalu masuk slip gaji bulanan; jam istirahat makan malam lembur tidak dihitung. Bonus project dibayar per 3 bulan (Mar/Jun/Sep/Des) untuk WO yang ditandai selesai; WO yang dibuka lagi karena komplain dihitung ulang dengan semua waktu & biaya tambahan.</p>
      <WeeklyRecap d={d} />
      <BonusRecap d={d} />
      <FinSettings d={d} load={load} />
    </div>
  );
}
